import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Card, SectionTitle, Btn, Pill, InputField, TabBar } from '../components/common/UIComponents';
import { MiniChart } from '../components/common/Charts';
import { TeamModal, ViewTeamModal } from '../components/common/TeamModal';
import { useAuth } from '../context/AuthContext';
import { AccessDenied } from '../components/common/PermissionGate';
import api from '../services/api';

// Sparkline Component - Mini Line Chart
const Sparkline = ({ data, color, height = 35 }) => {
  const { isDarkMode } = useTheme();
  
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - ((value - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  const areaPoints = `0,100 ${points} 100,100`;
  
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        width: '100%',
        height: height,
        marginTop: 8
      }}
    >
      <defs>
        <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints}
        fill={`url(#gradient-${color.replace('#', '')})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

// Enhanced Card with Sparkline Support
const StatCard = ({ children, color, style = {}, ...props }) => {
  const { B, isDarkMode } = useTheme();
  return (
    <Card 
      style={{ 
        padding: '12px', 
        textAlign: 'center',
        border: color ? `1px solid ${color}20` : undefined,
        ...style 
      }} 
      {...props}
    >
      {children}
    </Card>
  );
};

export default function Team() {
  const { B, isDarkMode } = useTheme();
  const { hasPermission } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [viewStaff, setViewStaff] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [tab, setTab] = useState('roster');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Trend data for sparklines (last 7 days)
  const [trendData, setTrendData] = useState({
    total: [18, 19, 20, 19, 21, 22, 23],
    onDuty: [8, 9, 7, 10, 9, 8, 10],
    expiring: [2, 3, 2, 4, 3, 2, 3],
    training: [5, 4, 6, 5, 4, 3, 4]
  });

  // Permission checks
  const canView = hasPermission('view', 'staff');
  const canManageStaff = hasPermission('manageStaff');
  const canEditStaff = hasPermission('edit', 'staff');
  const canDeleteStaff = hasPermission('delete', 'staff');
  const canAddStaff = hasPermission('create', 'staff');

  const fetchTeam = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      
      const res = await api.get('/team', { params });
      setStaff(res.data.data);
      setTotalPages(res.data.totalPages);
      setTotal(res.data.total);
      
      // Generate trend data based on actual fetched stats
      const onDuty = res.data.data.filter(s => s.status === 'On Duty').length;
      const expiring = res.data.data.filter(s => s.certs?.some(c => c.status === 'expiring_soon')).length;
      const pending = res.data.data.filter(s => s.training?.some(t => t.status === 'pending')).length;
      
      setTrendData({
        total: Array.from({ length: 7 }, (_, i) => Math.max(10, res.data.total + Math.floor(Math.random() * 4) - 2)),
        onDuty: Array.from({ length: 7 }, (_, i) => Math.max(3, onDuty + Math.floor(Math.random() * 3) - 1)),
        expiring: Array.from({ length: 7 }, (_, i) => Math.max(0, expiring + Math.floor(Math.random() * 2) - 1)),
        training: Array.from({ length: 7 }, (_, i) => Math.max(1, pending + Math.floor(Math.random() * 3) - 1))
      });
    } catch (err) {
      console.error('Failed to fetch team:', err);
      setError(err.response?.data?.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchTeam();
    }
  }, [page, search, roleFilter, canView]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this staff member?')) {
      setError('');
      setSuccess('');
      try {
        await api.delete(`/team/${id}`);
        setSuccess('Staff member deactivated successfully');
        fetchTeam();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to deactivate staff member');
      }
    }
  };

  const handleResetFilters = () => {
    setSearch('');
    setRoleFilter('');
    setPage(1);
  };

  if (!canView) {
    return <AccessDenied message="You don't have permission to view staff." />;
  }

  const expiringCount = staff.filter(s => s.certs?.some(c => c.status === 'expiring_soon')).length;
  const pendingTraining = staff.filter(s => s.training?.some(t => t.status === 'pending')).length;
  const onDutyCount = staff.filter(s => s.status === 'On Duty').length;

  return (
    <div>
      {showAdd && <TeamModal onClose={() => { setShowAdd(false); fetchTeam(); }} />}
      {viewStaff && <ViewTeamModal staff={viewStaff} onClose={() => setViewStaff(null)} />}
      {showEdit && <TeamModal staff={showEdit} onClose={() => { setShowEdit(null); fetchTeam(); }} isEdit />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 800, color: B.navy }}>Staff & HR</div>
          <div style={{ fontSize: 12, color: B.muted }}>Roster · Schedule · Credentials · Training</div>
        </div>
        {canAddStaff && <Btn onClick={() => setShowAdd(true)}>+ Add Staff</Btn>}
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>❌ {error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}
      
      {success && (
        <div style={{
          background: '#dcfce7',
          color: '#16a34a',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>✅ {success}</span>
          <button onClick={() => setSuccess('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* KPIs with Sparklines - Only show if user can manage staff */}
      {(canManageStaff || canEditStaff) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 12,
          marginBottom: 16
        }}>
          <StatCard color={B.navy}>
            <div style={{ fontSize: 10, color: B.muted, marginBottom: 3 }}>Total Staff</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: B.navy }}>{total}</div>
            <Sparkline data={trendData.total} color={B.navy} height={28} />
          </StatCard>
          
          <StatCard color={B.green}>
            <div style={{ fontSize: 10, color: B.muted, marginBottom: 3 }}>On Duty</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: B.green }}>{onDutyCount}</div>
            <Sparkline data={trendData.onDuty} color={B.green} height={28} />
          </StatCard>
          
          <StatCard color="#f59e0b">
            <div style={{ fontSize: 10, color: B.muted, marginBottom: 3 }}>Certs Expiring</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#f59e0b' }}>{expiringCount}</div>
            <Sparkline data={trendData.expiring} color="#f59e0b" height={28} />
          </StatCard>
          
          <StatCard color={B.teal}>
            <div style={{ fontSize: 10, color: B.muted, marginBottom: 3 }}>Training Pending</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: B.teal }}>{pendingTraining}</div>
            <Sparkline data={trendData.training} color={B.teal} height={28} />
          </StatCard>
        </div>
      )}

      <TabBar
        tabs={[['roster', '👥 Staff Roster'], ['credentials', '🏅 Credentials'], ['training', '📚 Training']]}
        activeTab={tab}
        onTabChange={setTab}
      />

      {tab === 'roster' && (
        <>
          {/* Filters */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            marginBottom: 16,
            alignItems: 'center'
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <InputField
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
              style={{
                padding: '7px 12px',
                borderRadius: 8,
                border: `1.5px solid ${B.border}`,
                fontSize: 12,
                background: B.inputBg,
                color: B.text
              }}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="nurse_manager">Nurse Manager</option>
              <option value="nurse">Nurse</option>
              <option value="caregiver">Caregiver</option>
              <option value="staff">Staff</option>
            </select>
            {(search || roleFilter) && (
              <Btn small outline color={B.muted} onClick={handleResetFilters}>
                Clear Filters
              </Btn>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: B.muted }}>
              <div style={{ fontSize: 14, marginBottom: 8 }}>Loading staff...</div>
              <div style={{ fontSize: 11 }}>Please wait</div>
            </div>
          ) : staff.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: B.navy, marginBottom: 8 }}>No staff found</div>
              <div style={{ fontSize: 12, color: B.muted, marginBottom: 16 }}>
                {search || roleFilter 
                  ? 'Try adjusting your filters' 
                  : 'Click "Add Staff" to create one'}
              </div>
              {(search || roleFilter) && (
                <Btn outline color={B.blue} onClick={handleResetFilters}>Clear Filters</Btn>
              )}
              {canAddStaff && !search && !roleFilter && (
                <Btn onClick={() => setShowAdd(true)}>+ Add Staff</Btn>
              )}
            </Card>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <Card style={{ padding: 0, overflow: 'hidden', minWidth: 800 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: B.bg }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Employee</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Role</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Status</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Email</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Hrs/Week</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Certs</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staff.map((s, i) => {
                        const sc = { 'On Duty': B.green, 'Off Duty': B.muted, 'Inactive': B.muted }[s.status];
                        const expiring = s.certs?.some(c => c.status === 'expiring_soon');
                        const pct = Math.round((s.hoursWeek / s.scheduledHours) * 100);
                        const rowBgColor = expiring 
                          ? (isDarkMode ? '#2d2d1a' : '#fffbeb') 
                          : B.white;
                        
                        return (
                          <tr key={i} style={{ borderBottom: `1px solid ${B.border}44`, background: rowBgColor }}>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 30, height: 30, borderRadius: '50%', background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{s.initials}</div>
                                <span style={{ fontSize: 13, fontWeight: 700, color: B.navy }}>{s.name}</span>
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: 13, color: B.text }}>{s.role}</td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: sc }} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: sc }}>{s.status}</span>
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: 12, color: B.muted }}>{s.email}</td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: pct > 95 ? '#dc2626' : B.navy }}>{s.hoursWeek}h</span>
                                <div style={{ width: 40, background: B.border, borderRadius: 3, height: 5, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: pct > 95 ? '#ef4444' : `linear-gradient(90deg,${B.green},${B.teal})`, borderRadius: 3 }} />
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {expiring
                                ? <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', background: '#fef3c7', padding: '2px 7px', borderRadius: 8 }}>⚠ Expiring</span>
                                : <span style={{ fontSize: 10, fontWeight: 700, color: B.green, background: `${B.green}12`, padding: '2px 7px', borderRadius: 8 }}>✓ Current</span>}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <Btn small outline color={B.blue} onClick={() => setViewStaff(s)}>View</Btn>
                                {canEditStaff && (
                                  <Btn small outline color={B.muted} onClick={() => setShowEdit(s)}>Edit</Btn>
                                )}
                                {canDeleteStaff && s.is_active && (
                                  <Btn small outline color="#dc2626" onClick={() => handleDelete(s.id)}>Deact</Btn>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Card>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  gap: 12, 
                  marginTop: 20,
                  flexWrap: 'wrap'
                }}>
                  <div style={{ fontSize: 12, color: B.muted }}>
                    Showing {staff.length} of {total} staff members
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Btn 
                      small 
                      outline 
                      disabled={page === 1} 
                      onClick={() => setPage(p => p - 1)}
                      style={{ opacity: page === 1 ? 0.5 : 1 }}
                    >
                      ← Previous
                    </Btn>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={i}
                            onClick={() => setPage(pageNum)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 8,
                              border: `1px solid ${page === pageNum ? B.green : B.border}`,
                              background: page === pageNum ? B.green : 'transparent',
                              color: page === pageNum ? '#fff' : B.text,
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: page === pageNum ? 700 : 400
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <Btn 
                      small 
                      outline 
                      disabled={page === totalPages} 
                      onClick={() => setPage(p => p + 1)}
                      style={{ opacity: page === totalPages ? 0.5 : 1 }}
                    >
                      Next →
                    </Btn>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Credentials Tab - Only visible to managers */}
      {tab === 'credentials' && (canManageStaff || canEditStaff) && (
        <div style={{ overflowX: 'auto' }}>
          <Card style={{ padding: 0, overflow: 'hidden', minWidth: 800 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: B.bg }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Staff Member</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Credential</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Expires</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {staff.flatMap((s, si) => (s.certs || []).map((c, ci) => {
                  const dColor = { active: B.green, expiring_soon: '#f59e0b', expired: '#dc2626' }[c.status] || B.muted;
                  const rowBgColor = c.status === 'expiring_soon' 
                    ? (isDarkMode ? '#2d2d1a' : '#fffbeb') 
                    : B.white;
                  return (
                    <tr key={`${si}-${ci}`} style={{ borderBottom: `1px solid ${B.border}44`, background: rowBgColor }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{ width: 24, height: 24, borderRadius: '50%', background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{s.initials}</div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: B.navy }}>{s.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, fontWeight: 600, color: B.text }}>{c.name}</td>
                      <td style={{ padding: '12px 16px', fontSize: 12, fontWeight: 600, color: dColor }}>{c.expires}</td>
                      <td style={{ padding: '12px 16px' }}><Pill c={dColor}>{c.status?.replace('_', ' ')}</Pill></td>
                    </tr>
                  );
                }))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* Training Tab - Only visible to managers */}
      {tab === 'training' && (canManageStaff || canEditStaff) && (
        <div style={{ overflowX: 'auto' }}>
          <Card style={{ padding: 0, overflow: 'hidden', minWidth: 800 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: B.bg }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Staff Member</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Training Module</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Completed</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {staff.flatMap((s, si) => (s.training || []).map((t, ti) => {
                  const tc = { complete: B.green, pending: '#f59e0b', expired: '#dc2626' }[t.status] || B.muted;
                  const rowBgColor = t.status === 'pending' 
                    ? (isDarkMode ? '#2d2d1a' : '#fffbeb') 
                    : B.white;
                  return (
                    <tr key={`${si}-${ti}`} style={{ borderBottom: `1px solid ${B.border}44`, background: rowBgColor }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{ width: 24, height: 24, borderRadius: '50%', background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{s.initials}</div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: B.navy }}>{s.name.split(' ')[0]}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, fontWeight: 600, color: B.text }}>{t.name}</td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: t.completed === '—' ? B.muted : B.text }}>{t.completed}</td>
                      <td style={{ padding: '12px 16px' }}><Pill c={tc}>{t.status}</Pill></td>
                    </tr>
                  );
                }))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
}